<template>
  <div class="screen-wrapper" ref="screenWrapper" :style="wrapperStyle">
    <slot></slot>
  </div>
</template>

<script>
function debounce(fn, delay) {
  let timer = null;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (typeof fn === "function") {
        fn.apply(this, args);
      }
    }, delay > 0 ? delay : 100);
  };
}

export default {
  name: "VScaleScreen",
  props: {
    width: {
      type: [String, Number],
      default: 1280,
    },
    height: {
      type: [String, Number],
      default: 700,
    },
    fullScreen: {
      type: Boolean,
      default: false,
    },
    autoScale: {
      type: [Object, Boolean],
      default: true,
    },
    selfAdaption: {
      type: Boolean,
      default: true,
    },
    delay: {
      type: Number,
      default: 300,
    },
    wrapperStyle: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      currentWidth: 0,
      currentHeight: 0,
      originalWidth: 0,
      originalHeight: 0,
      onResize: null,
    };
  },
  watch: {
    selfAdaption(val) {
      if (val) {
        this.resize();
        this.addListener();
      } else {
        this.clearListener();
        this.clearStyle();
      }
    },
    width() {
      this.resize();
    },
    height() {
      this.resize();
    },
    fullScreen() {
      this.resize();
    },
    autoScale() {
      this.resize();
    },
  },
  computed: {
    screenWrapper() {
      return this.$refs.screenWrapper;
    },
  },
  methods: {
    initSize() {
      return new Promise((resolve) => {
        const wrapper = this.screenWrapper;
        if (!wrapper || !wrapper.parentNode) {
          resolve();
          return;
        }

        wrapper.parentNode.style.overflow = "hidden";
        wrapper.parentNode.scrollLeft = 0;
        wrapper.parentNode.scrollTop = 0;

        this.$nextTick(() => {
          if (this.width && this.height) {
            this.currentWidth = Number(this.width);
            this.currentHeight = Number(this.height);
          } else {
            this.currentWidth = wrapper.clientWidth;
            this.currentHeight = wrapper.clientHeight;
          }

          if (!this.originalHeight || !this.originalWidth) {
            this.originalWidth = window.innerWidth;
            this.originalHeight = window.innerHeight;
          }
          resolve();
        });
      });
    },
    updateSize() {
      const wrapper = this.screenWrapper;
      if (!wrapper) return;

      const width = this.currentWidth || this.originalWidth;
      const height = this.currentHeight || this.originalHeight;
      wrapper.style.width = `${width}px`;
      wrapper.style.height = `${height}px`;
    },
    handleAutoScale(scale) {
      if (!this.autoScale) return;
      const wrapper = this.screenWrapper;
      if (!wrapper) return;

      const domWidth = this.currentWidth || wrapper.clientWidth;
      const domHeight = this.currentHeight || wrapper.clientHeight;
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;

      let mx = Math.max((currentWidth - domWidth * scale) / 2, 0);
      let my = Math.max((currentHeight - domHeight * scale) / 2, 0);

      if (typeof this.autoScale === "object") {
        if (!this.autoScale.x) mx = 0;
        if (!this.autoScale.y) my = 0;
      }

      wrapper.style.margin = `${my}px ${mx}px`;
    },
    updateScale() {
      const wrapper = this.screenWrapper;
      if (!wrapper) return;

      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;
      const realWidth = this.currentWidth || this.originalWidth;
      const realHeight = this.currentHeight || this.originalHeight;

      const widthScale = currentWidth / realWidth;
      const heightScale = currentHeight / realHeight;

      if (this.fullScreen) {
        wrapper.style.transform = `scale(${widthScale},${heightScale})`;
        wrapper.style.margin = "0px";
        return;
      }

      const scale = Math.min(widthScale, heightScale);
      wrapper.style.transform = `scale(${scale})`;
      this.handleAutoScale(scale);
    },
    clearListener() {
      window.removeEventListener("resize", this.onResize);
    },
    addListener() {
      window.addEventListener("resize", this.onResize);
    },
    clearStyle() {
      const wrapper = this.screenWrapper;
      if (!wrapper || !wrapper.parentNode) return;

      wrapper.parentNode.style.overflow = "auto";
      wrapper.style.width = "";
      wrapper.style.height = "";
      wrapper.style.margin = "";
      wrapper.style.transform = "";
    },
    async resize() {
      if (!this.selfAdaption) return;
      await this.initSize();
      this.updateSize();
      this.updateScale();
    },
  },
  mounted() {
    this.onResize = debounce(() => {
      this.resize();
    }, this.delay);

    this.$nextTick(() => {
      if (this.selfAdaption) {
        this.resize();
        this.addListener();
      }
    });
  },
  beforeDestroy() {
    this.clearListener();
  },
};
</script>

<style scoped>
.screen-wrapper {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
  position: relative;
  overflow: hidden;
  z-index: 100;
  transform-origin: left top;
}
</style>


